import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Edit2, Trash2, Paperclip, ArrowDownLeft, ArrowUpRight, FileText, Upload } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY_FORM = {
  type: 'in',
  date: new Date().toISOString().split('T')[0],
  reference: '',
  sender: '',
  recipient: '',
  subject: '',
  summary: '',
  attachment_url: '',
  attachment_name: ''
};

export default function CorrespondenceManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [filter, setFilter] = useState('all');
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [uploading, setUploading] = useState(false);

  const fetchItems = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/correspondences`, { withCredentials: true });
      setItems(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/admin/correspondences/upload`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData(prev => ({
        ...prev,
        attachment_url: res.data.url,
        attachment_name: res.data.filename
      }));
      toast.success('Pièce jointe téléversée');
    } catch (err) { toast.error('Erreur upload'); }
    finally { setUploading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API}/admin/correspondences/${editingId}`, formData, { withCredentials: true });
        toast.success('Courrier mis à jour');
      } else {
        await axios.post(`${API}/admin/correspondences`, formData, { withCredentials: true });
        toast.success('Courrier enregistré');
      }
      resetForm();
      fetchItems();
    } catch (e) { toast.error('Erreur'); }
  };

  const handleEdit = (item) => {
    setEditingId(item.correspondence_id);
    setFormData({
      type: item.type,
      date: item.date,
      reference: item.reference,
      sender: item.sender,
      recipient: item.recipient,
      subject: item.subject,
      summary: item.summary || '',
      attachment_url: item.attachment_url || '',
      attachment_name: item.attachment_name || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce courrier ?')) return;
    try {
      await axios.delete(`${API}/admin/correspondences/${id}`, { withCredentials: true });
      toast.success('Courrier supprimé');
      fetchItems();
    } catch (e) { toast.error('Erreur'); }
  };

  const resetForm = () => {
    setFormData(EMPTY_FORM);
    setEditingId(null);
    setShowForm(false);
  };

  const filtered = items.filter(i => filter === 'all' || i.type === filter);
  const counts = {
    all: items.length,
    in: items.filter(i => i.type === 'in').length,
    out: items.filter(i => i.type === 'out').length,
  };

  return (
    <div data-testid="correspondence-manager">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-[#2E332D]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          Registre de Correspondance ({items.length})
        </h2>
        <button onClick={() => setShowForm(!showForm)} className="btn-primary inline-flex items-center" data-testid="add-correspondence-button">
          <Plus size={20} className="mr-2" />{showForm ? 'Annuler' : 'Nouveau courrier'}
        </button>
      </div>

      <div className="flex gap-2 mb-6">
        {[
          { id: 'all', label: 'Tous' },
          { id: 'in', label: '📥 Courrier reçu' },
          { id: 'out', label: '📤 Courrier expédié' }
        ].map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`px-4 py-2 rounded-full text-sm transition-all ${
              filter === f.id ? 'bg-[#4A6B48] text-white' : 'bg-white border border-[#E5E3DB] text-[#5C665A]'
            }`}
            data-testid={`corr-filter-${f.id}`}
          >
            {f.label} ({counts[f.id]})
          </button>
        ))}
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="card mb-6" data-testid="correspondence-form">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#2E332D] mb-2">Type *</label>
                <select value={formData.type} onChange={(e) => setFormData({...formData, type: e.target.value})} className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" data-testid="corr-type-select">
                  <option value="in">📥 Courrier reçu (IN)</option>
                  <option value="out">📤 Courrier expédié (OUT)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2E332D] mb-2">Date *</label>
                <input type="date" required value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#2E332D] mb-2">Numéro de référence *</label>
              <input type="text" required placeholder="Ex: CRESA/2026/0001" value={formData.reference} onChange={(e) => setFormData({...formData, reference: e.target.value})} className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" data-testid="corr-reference-input" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#2E332D] mb-2">Expéditeur *</label>
                <input type="text" required placeholder="Nom / Organisation" value={formData.sender} onChange={(e) => setFormData({...formData, sender: e.target.value})} className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2E332D] mb-2">Destinataire *</label>
                <input type="text" required placeholder="Nom / Organisation" value={formData.recipient} onChange={(e) => setFormData({...formData, recipient: e.target.value})} className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#2E332D] mb-2">Objet *</label>
              <input type="text" required value={formData.subject} onChange={(e) => setFormData({...formData, subject: e.target.value})} className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" data-testid="corr-subject-input" />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#2E332D] mb-2">Résumé / Notes</label>
              <textarea rows={3} value={formData.summary} onChange={(e) => setFormData({...formData, summary: e.target.value})} className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#2E332D] mb-2">Pièce jointe (PDF / Image)</label>
              <div className="flex items-center gap-3">
                <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 border border-[#E5E3DB] rounded-lg hover:bg-[#F9F8F6]">
                  <Upload size={16} />
                  <span className="text-sm">{uploading ? 'Téléversement...' : 'Choisir un fichier'}</span>
                  <input type="file" accept=".pdf,image/*" onChange={handleFileUpload} className="hidden" data-testid="corr-attachment-input" />
                </label>
                {formData.attachment_name && (
                  <span className="text-sm text-[#4A6B48] flex items-center gap-1">
                    <Paperclip size={14} />{formData.attachment_name}
                  </span>
                )}
              </div>
            </div>

            <div className="flex gap-2">
              <button type="submit" className="btn-primary" data-testid="submit-correspondence">{editingId ? 'Mettre à jour' : 'Enregistrer'}</button>
              <button type="button" onClick={resetForm} className="btn-secondary">Annuler</button>
            </div>
          </div>
        </form>
      )}

      {loading && <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4A6B48]"></div></div>}

      {!loading && filtered.length === 0 && (
        <p className="text-center text-[#5C665A] py-8">Aucun courrier dans cette catégorie.</p>
      )}

      {!loading && filtered.length > 0 && (
        <div className="space-y-3">
          {filtered.map((item) => {
            const isIn = item.type === 'in';
            return (
              <div key={item.correspondence_id} className="card" data-testid={`corr-item-${item.correspondence_id}`}>
                <div className="flex justify-between items-start gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <span className={`text-xs font-bold px-2 py-1 rounded inline-flex items-center gap-1 ${
                        isIn ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'
                      }`}>
                        {isIn ? <ArrowDownLeft size={12} /> : <ArrowUpRight size={12} />}
                        {isIn ? 'REÇU' : 'EXPÉDIÉ'}
                      </span>
                      <span className="text-xs font-mono text-[#5C665A]">{item.reference}</span>
                      <span className="text-xs text-[#5C665A]">{new Date(item.date).toLocaleDateString('fr-FR')}</span>
                    </div>
                    <h3 className="text-lg font-bold text-[#2E332D]">{item.subject}</h3>
                    <div className="mt-2 text-sm text-[#5C665A] grid grid-cols-2 gap-2">
                      <div><strong>De:</strong> {item.sender}</div>
                      <div><strong>À:</strong> {item.recipient}</div>
                    </div>
                    {item.summary && <p className="text-sm text-[#5C665A] mt-2 italic">{item.summary}</p>}
                    {item.attachment_url && (
                      <a href={item.attachment_url} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-2 text-sm text-[#4A6B48] hover:underline">
                        <FileText size={14} />
                        {item.attachment_name || 'Voir la pièce jointe'}
                      </a>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => handleEdit(item)} className="p-2 hover:bg-gray-100 rounded"><Edit2 size={18} className="text-[#4A6B48]" /></button>
                    <button onClick={() => handleDelete(item.correspondence_id)} className="p-2 hover:bg-gray-100 rounded"><Trash2 size={18} className="text-red-500" /></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
